'use client';

import { get as _get } from 'lodash-es';
import { useParams } from 'next/navigation';
import { useQuery } from '@apollo/client/react';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Loading from '@/components/loading';
import ErrorMessage from '@/components/error-message';
import NotFound from '@/components/not-found';
import EventList from '@/components/event-list';
import { groupCalendarQuery } from '@/apollo/groups';
import CalendarHeader from '@/components/groups/calendar-header';
import GroupCalendarFooter from '@/components/groups/calendar-footer';

type Props = {
  initialGroup?: Record<string, unknown>;
};

const GroupCalendarPageClient = ({ initialGroup }: Props) => {
  const { slug } = useParams();

  const { data, loading, error } = useQuery(groupCalendarQuery, {
    variables: { slug },
    ssr: false,
  });
  const group = _get(data, 'group') || initialGroup;

  if (!group && loading) { return <Loading />; }
  if (error && !group) {
    return <ErrorMessage message='An error occurred while loading group.' error={error} />;
  }
  if (!group || group.found) { return <NotFound />; }

  return (
    <>
      <CalendarHeader title="Calendar" tab="upcoming" group={group} />

      <Typography variant="h2" sx={{ display: 'flex', alignItems: 'center' }}>
        Upcoming
        {Boolean(group.pendingUpcomingEventsCount) && group.pendingUpcomingEventsCount > 0 && (
          <Chip
            sx={{
              ml: 1,
              backgroundColor: '#e53935',
              color: '#fff',
              fontWeight: 'normal',
              letterSpacing: '0.14994px',
            }}
            label={`${group.pendingUpcomingEventsCount} Pending Approval`}
            size="small"
          />
        )}
      </Typography>

      <EventList
        events={group.upcomingEvents}
        group={group}
        emptyMessage={
          group.permissions.joined ? 'No upcoming events' : 'No upcoming public events'
        }
        fullDate
      />

      <GroupCalendarFooter group={group} />
    </>
  );
};

export default GroupCalendarPageClient;
